import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { CheckCircle2, XCircle, Mail, Loader2 } from 'lucide-react';

const VerifyEmail = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { verifyEmail } = useAuth();
  const ranRef = useRef(false);
  const [status, setStatus] = useState('verifying'); // verifying | success | error | missing
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    const params = new URLSearchParams(location.search);
    const token = params.get('token');
    if (!token) {
      setStatus('missing');
      setMessage('No verification token found in the link.');
      return;
    }
    (async () => {
      const r = await verifyEmail(token);
      if (r.success) {
        setStatus('success');
        setMessage('Your email has been verified. Welcome to Aptivon!');
        setTimeout(() => navigate('/dashboard', { replace: true }), 1500);
      } else {
        setStatus('error');
        setMessage(r.error || 'Verification link is invalid or expired.');
      }
    })();
  }, [location.search, verifyEmail, navigate]);

  return (
    <div
      className="min-h-screen flex items-center justify-center px-6 py-12"
      style={{ background: 'linear-gradient(135deg, rgba(14, 165, 233, 0.1) 0%, rgba(251, 191, 36, 0.1) 100%)' }}
    >
      <div className="w-full max-w-md">
        <div className="glass-card p-8 shadow-xl text-center" data-testid="verify-email-card">
          {status === 'verifying' && (
            <>
              <Loader2 className="w-14 h-14 mx-auto text-[#0EA5E9] animate-spin" />
              <h1 className="mt-4 text-2xl font-bold text-[#111827]">Verifying your email…</h1>
              <p className="mt-2 text-[#6B7280]">Hang tight, this only takes a moment.</p>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle2 className="w-14 h-14 mx-auto text-emerald-500" data-testid="verify-success-icon" />
              <h1 className="mt-4 text-2xl font-bold text-[#111827]" data-testid="verify-success-title">Email verified!</h1>
              <p className="mt-2 text-[#6B7280]" data-testid="verify-success-message">{message}</p>
              <p className="mt-4 text-sm text-[#9CA3AF]">Redirecting you to your dashboard…</p>
            </>
          )}

          {(status === 'error' || status === 'missing') && (
            <>
              <XCircle className="w-14 h-14 mx-auto text-red-500" data-testid="verify-error-icon" />
              <h1 className="mt-4 text-2xl font-bold text-[#111827]">Verification failed</h1>
              <p className="mt-2 text-[#6B7280]" data-testid="verify-error-message">{message}</p>
              <div className="mt-6 flex flex-col gap-3">
                <Link
                  to="/login"
                  className="px-6 py-3 bg-[#0EA5E9] text-white rounded-2xl font-bold shadow-[0_4px_0_rgb(2,132,199)] active:translate-y-1 active:shadow-none transition-all flex items-center justify-center gap-2"
                  data-testid="verify-go-login"
                >
                  <Mail className="w-5 h-5" /> Go to Login
                </Link>
                <Link
                  to="/register"
                  className="text-[#0EA5E9] font-bold hover:underline"
                  data-testid="verify-go-register"
                >
                  Or create a new account
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
